import { supabase, handleSupabaseError, handleSupabaseSuccess } from '../lib/supabase';
import { User, UserRole, UserStatus, UserFilters, ApiResponse, PaginatedResponse } from '../types';

// ============================================================================
// SUPABASE USER MANAGEMENT SERVICE
// ============================================================================

class UserService {
  private static instance: UserService;

  private constructor() {}

  public static getInstance(): UserService {
    if (!UserService.instance) {
      UserService.instance = new UserService();
    }
    return UserService.instance;
  }

  /**
   * Helper function to format user display name
   * Shows username with @ prefix, or email prefix as fallback
   */
  private formatDisplayName(username: string | null, email: string): string {
    if (username && username.trim()) {
      return `@${username.trim()}`;
    }
    // Fallback to email prefix (part before @)
    return email.split('@')[0];
  }

  // ============================================================================
  // USER FETCHING AND FILTERING
  // ============================================================================

  private mapRowToUser(row: any): User {
    return {
      id: row.id,
      email: row.email,
      name: this.formatDisplayName(row.username, row.email),
      username: row.username || undefined,
      fullName: row.full_name || undefined,
      displayName: row.display_name || undefined,
      role: row.role === 'teacher' ? 'creator' as UserRole : row.role as UserRole,
      status: row.is_active !== false ? 'active' as UserStatus : 'inactive' as UserStatus,
      registrationDate: new Date(row.created_at),
      lastLoginDate: row.last_login_at ? new Date(row.last_login_at) : undefined,
      profileImage: row.profile_icon_url || row.avatar_url || undefined,
      phoneNumber: row.phone || undefined,
      bio: row.bio || undefined,
      socialMediaHandle: row.social_media || undefined,
      expertiseAreas: row.expertise_areas || [],
      applicationStatus: row.application_status || undefined,
      applicationDate: row.application_submitted_at ? new Date(row.application_submitted_at) : undefined,
      applicationNotes: row.admin_notes || undefined,
      subscriptionTier: row.subscription_tier || undefined,
      subscriptionExpiresAt: row.subscription_expires_at ? new Date(row.subscription_expires_at) : undefined,
      totalVideos: row.total_videos_watched || 0,
      totalViews: 0,
      totalQuizzes: row.total_quizzes_completed || 0,
      averageRating: 0,
    };
  }

  /**
   * Get paginated users with filters from live Supabase
   */
  async getUsers(
    page: number = 0,
    pageSize: number = 10,
    filters: UserFilters = {}
  ): Promise<ApiResponse<PaginatedResponse<User>>> {
    try {
      let query = supabase
        .from('profiles')
        .select('*', { count: 'exact' });

      if (filters.role) {
        const dbRole = filters.role === 'creator' ? 'teacher' : filters.role;
        query = query.eq('role', dbRole);
      }

      if (filters.status) {
        const isActive = filters.status === 'active';
        query = query.eq('is_active', isActive);
      }

      if (filters.applicationStatus) {
        (query as any) = (query as any).eq('application_status', filters.applicationStatus);
      }

      if (filters.searchQuery) {
        query = query.or(`username.ilike.%${filters.searchQuery}%,email.ilike.%${filters.searchQuery}%`);
      }

      if (filters.registrationDateFrom) {
        query = query.gte('created_at', filters.registrationDateFrom.toISOString());
      }

      if (filters.registrationDateTo) {
        query = query.lte('created_at', filters.registrationDateTo.toISOString());
      }

      const from = page * pageSize;
      const to = from + pageSize - 1;

      query = query
        .range(from, to)
        .order('created_at', { ascending: false });

      const { data, error, count } = await query;

      if (error) {
        return handleSupabaseError<PaginatedResponse<User>>(error);
      }

      const users: User[] = (data || []).map(row => this.mapRowToUser(row));

      // Fetch real activity counts from video_progress and quiz_attempts
      if (users.length > 0) {
        const userIds = users.map(u => u.id);

        const [videoProgressRes, quizAttemptsRes] = await Promise.all([
          supabase.from('video_progress').select('user_id').in('user_id', userIds),
          supabase.from('quiz_attempts').select('user_id').in('user_id', userIds),
        ]);

        const videoCounts = new Map<string, number>();
        for (const row of videoProgressRes.data || []) {
          videoCounts.set(row.user_id, (videoCounts.get(row.user_id) || 0) + 1);
        }

        const quizCounts = new Map<string, number>();
        for (const row of quizAttemptsRes.data || []) {
          quizCounts.set(row.user_id, (quizCounts.get(row.user_id) || 0) + 1);
        }

        for (const user of users) {
          user.totalVideos = videoCounts.get(user.id) || 0;
          user.totalQuizzes = quizCounts.get(user.id) || 0;
        }
      }

      const totalPages = Math.ceil((count || 0) / pageSize);

      return handleSupabaseSuccess({
        data: users,
        pagination: {
          currentPage: page,
          totalPages,
          totalItems: count || 0,
          itemsPerPage: pageSize,
        }
      });
    } catch (error) {
      return handleSupabaseError<PaginatedResponse<User>>(error);
    }
  }

  /**
   * Get user by ID
   */
  async getUserById(userId: string): Promise<ApiResponse<User>> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        return handleSupabaseError<User>(error);
      }

      return handleSupabaseSuccess(this.mapRowToUser(data));
    } catch (error) {
      return handleSupabaseError<User>(error);
    }
  }

  /**
   * Get pending creator applications
   */
  async getPendingApplications(): Promise<ApiResponse<User[]>> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('role', 'teacher')
        .eq('application_status', 'pending')
        .order('application_submitted_at', { ascending: false });

      if (error) {
        return handleSupabaseError<User[]>(error);
      }

      const users: User[] = (data || []).map(row => this.mapRowToUser(row));
      return handleSupabaseSuccess(users);
    } catch (error) {
      return handleSupabaseError<User[]>(error);
    }
  }

  // ============================================================================
  // USER MANAGEMENT ACTIONS
  // ============================================================================

  /**
   * Update user role
   */
  async updateUserRole(userId: string, newRole: UserRole): Promise<ApiResponse<void>> {
    try {
      // Map admin portal roles to database roles
      const dbRole = newRole === 'creator' ? 'teacher' : newRole;
      
      const { error } = await supabase
        .from('profiles')
        .update({ 
          role: dbRole,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (error) {
        return handleSupabaseError<void>(error);
      }

      return handleSupabaseSuccess(undefined, `User role updated to ${newRole}`);
    } catch (error) {
      return handleSupabaseError<void>(error);
    }
  }

  /**
   * Update user status (active/inactive using is_active field)
   */
  async updateUserStatus(
    userId: string, 
    status: UserStatus, 
    reason?: string
  ): Promise<ApiResponse<void>> {
    try {
      const updateData: any = {
        is_active: status === 'active',
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', userId);

      if (error) {
        return handleSupabaseError<void>(error);
      }

      return handleSupabaseSuccess(undefined, `User ${status === 'active' ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      return handleSupabaseError<void>(error);
    }
  }

  /**
   * Update application status (approve/reject creator applications)
   */
  async updateApplicationStatus(
    userId: string, 
    status: 'approved' | 'rejected', 
    reason?: string
  ): Promise<ApiResponse<void>> {
    try {
      const updateData: any = {
        application_status: status,
        updated_at: new Date().toISOString(),
      };

      // Add reason if provided
      if (reason) {
        updateData.admin_notes = reason;
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', userId);

      if (error) {
        return handleSupabaseError<void>(error);
      }

      return handleSupabaseSuccess(undefined, `Application ${status} successfully`);
    } catch (error) {
      return handleSupabaseError<void>(error);
    }
  }

  /**
   * Bulk approve creator applications
   */
  async bulkApproveApplications(userIds: string[]): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          application_status: 'approved',
          updated_at: new Date().toISOString(),
        })
        .in('id', userIds);

      if (error) {
        return handleSupabaseError<void>(error);
      }

      return handleSupabaseSuccess(undefined, `Successfully approved ${userIds.length} applications`);
    } catch (error) {
      return handleSupabaseError<void>(error);
    }
  }

  /**
   * Bulk approve creator applications - Convert students to teachers
   */
  async bulkApproveCreators(userIds: string[]): Promise<ApiResponse<void>> {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          role: 'teacher', // Use database role
          updated_at: new Date().toISOString(),
        })
        .in('id', userIds);

      if (error) {
        return handleSupabaseError<void>(error);
      }

      return handleSupabaseSuccess(undefined, `Successfully promoted ${userIds.length} users to creators`);
    } catch (error) {
      return handleSupabaseError<void>(error);
    }
  }

  /**
   * Update user profile information
   */
  async updateUserProfile(
    userId: string,
    profileData: Partial<User>
  ): Promise<ApiResponse<void>> {
    try {
      const updateData: any = {
        updated_at: new Date().toISOString(),
      };

      // Map frontend fields to database fields
      if (profileData.name !== undefined) {
        // If name starts with @, remove it to get the username
        const username = profileData.name.startsWith('@') ? profileData.name.slice(1) : profileData.name;
        updateData.username = username;
      }
      if (profileData.email !== undefined) updateData.email = profileData.email;
      if (profileData.phoneNumber !== undefined) updateData.phone = profileData.phoneNumber;
      if (profileData.bio !== undefined) updateData.bio = profileData.bio;
      if (profileData.profileImage !== undefined) updateData.avatar_url = profileData.profileImage;

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', userId);

      if (error) {
        return handleSupabaseError<void>(error);
      }

      return handleSupabaseSuccess(undefined, 'User profile updated successfully');
    } catch (error) {
      return handleSupabaseError<void>(error);
    }
  }

  // ============================================================================
  // STATISTICS AND ANALYTICS
  // ============================================================================

  /**
   * Get user statistics
   */
  async getUserStats(): Promise<ApiResponse<{
    totalUsers: number;
    totalCreators: number;
    totalStudents: number;
    pendingApplications: number;
    activeUsersToday: number;
  }>> {
    try {
      // Get total counts using parallel queries
      const [
        { count: totalUsers },
        { count: totalCreators },
        { count: totalStudents },
        { count: pendingApplications },
        { count: activeUsersToday }
      ] = await Promise.all([
        supabase.from('profiles').select('*', { count: 'exact', head: true }),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('role', 'teacher'),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('role', 'student'),
        supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('role', 'teacher').eq('application_status', 'pending'),
        supabase.from('profiles').select('*', { count: 'exact', head: true })
          .eq('is_active', true)
          .gte('last_login_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString())
      ]);

      return handleSupabaseSuccess({
        totalUsers: totalUsers || 0,
        totalCreators: totalCreators || 0,
        totalStudents: totalStudents || 0,
        pendingApplications: pendingApplications || 0,
        activeUsersToday: activeUsersToday || 0,
      });
    } catch (error) {
      return handleSupabaseError<{
        totalUsers: number;
        totalCreators: number;
        totalStudents: number;
        pendingApplications: number;
        activeUsersToday: number;
      }>(error);
    }
  }

  // ============================================================================
  // REAL-TIME SUBSCRIPTIONS
  // ============================================================================

  /**
   * Subscribe to user changes
   */
  subscribeToUserChanges(callback: (payload: any) => void) {
    return supabase
      .channel('users-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'profiles' },
        callback
      )
      .subscribe();
  }

  /**
   * Subscribe to pending applications
   */
  subscribeToPendingApplications(callback: (payload: any) => void) {
    return supabase
      .channel('pending-applications')
      .on('postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'profiles',
          filter: 'role=eq.teacher,application_status=eq.pending'
        },
        callback
      )
      .subscribe();
  }
}

// Export singleton instance
export const userService = UserService.getInstance();

